import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { db } from "@lyder-talk/core/lib/db-client";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "@/hooks/use-auth";

/**
 * The words the learner has saved, and nothing else.
 *
 * Used to highlight deck words inside example sentences. The full deck
 * (`useSavedVocabulary`) pulls every column of every row — definitions, review
 * state, enrichment payloads — which is far more than a word list needs, so
 * this hook selects the `word` column alone.
 *
 * The key sits under `queryKeys.savedVocabulary.all`, so any save/remove
 * mutation invalidates it along with the rest of the deck.
 */
export const useDeckWords = (): string[] => {
    const { userId, isAuthenticated } = useAuth();

    const { data } = useQuery<string[]>({
        queryKey: queryKeys.savedVocabulary.words(userId),
        enabled: Boolean(isAuthenticated && userId),
        queryFn: async (): Promise<string[]> => {
            const { data, error } = await db.from("saved_vocabulary")
                .select("word")
                .eq("user_id", userId as string);

            if (error) throw error;

            return ((data || []) as { word: string }[])
                .map((row) => row.word?.trim())
                .filter((word): word is string => Boolean(word));
        },
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
    });

    return useMemo(() => data ?? [], [data]);
};
